'use client';

// react
import { useCallback, useMemo, useState } from 'react';
// @mui
import {
  Container,
  Button,
  Stack,
  Typography,
  Grid,
  Rating,
  LinearProgress,
  Card,
  CardHeader,
  CardContent,
  Box,
  Link as MuiLink,
  Paper,
  List,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
// hooks
import { useDialog, useFetch, useModal } from '@/hooks';
// next
import dynamic from 'next/dynamic';
// services
import {
  DashboardUserRatingService,
  ContentsService,
  DashboardUserGroupsContentsUserRatingsService,
} from '@/services';
// components
import { Loading } from '@/components/common';
const UserRating = dynamic(() => import('@/components/common/UserRating'));
// react-toastify
import { toast } from 'react-toastify';
// lodash
import orderBy from 'lodash.orderby';

export default function ContentUserRatings({
  contentId,
  userGroupId,
  mutate,
  ratingActive,
  ratingScore,
  ratingCount,
}) {
  const {
    rate: { setOpen: setRateModalOpen, setProps: setRateModalProps },
  } = useModal();

  const { openDialog, closeDialog } = useDialog('Do you want to delete your rating?', {
    variant: 'error',
  });

  const [ratingStatistics, setRatingStatistics] = useState({});
  const [ratingList, setRatingList] = useState([]);
  const [ratingListMeta, setRatingListMeta] = useState({});
  const [page, setPage] = useState(1);

  const [reviewInfoBoxOpen, setReviewInfoBoxOpen] = useState(false);

  const fetchRatings = useCallback(async () => {
    const [
      { data: ratingScoreData = {}, success: ratingScoreSuccess },
      { data: ratingListData = [], meta: ratingListMeta = {}, success: ratingListSuccess },
    ] = await Promise.all([
      ContentsService.getRatesSummary(contentId),
      ContentsService.getRates(contentId, page, 10),
    ]);
    if (ratingScoreSuccess) {
      setRatingStatistics(ratingScoreData);
    }
    if (ratingListSuccess) {
      setRatingList((prevData) => {
        const prevDataIds = prevData.map(({ id }) => id);
        const filteredRatingListData = ratingListData.filter(
          ({ id }) => prevDataIds.indexOf(id) === -1
        );
        const orderedList = orderBy(
          [...prevData, ...filteredRatingListData],
          ['createdAt'],
          'desc'
        );
        return orderedList;
      });
      setRatingListMeta(ratingListMeta);
    }
  }, [page, contentId]);

  const deleteAndMutate = useCallback(
    async ({ id }) => {
      if (!id) return;

      setRatingList((ratingList) => {
        const filteredRatingListData = ratingList.filter(({ id: _id }) => id !== _id);
        return [...filteredRatingListData];
      });

      await fetchRatings();
    },
    [fetchRatings]
  );

  const handleViewMoreRatings = useCallback(() => {
    setPage((page) => page + 1);
  }, []);

  const handleToggleReviewInfo = useCallback(() => {
    setReviewInfoBoxOpen((open) => !open);
  }, []);

  const handleRate = useCallback(
    async (data) => {
      const { success } = await ContentsService.postRates(contentId, data);
      if (success) {
        await mutate();
        await fetchRatings();
        toast.success('Content rated successfully!');
        setRateModalOpen(false);
      }
    },
    [fetchRatings, mutate, setRateModalOpen, contentId]
  );
  const handleEditRate = useCallback(
    (id) => async (data) => {
      const { success } = await DashboardUserRatingService.putUserRating(id, data);
      if (success) {
        await mutate();
        await fetchRatings();
        toast.success('Rating updated successfully!');
        setRateModalOpen(false);
      }
    },
    [fetchRatings, mutate, setRateModalOpen]
  );

  const handleEditRating = useCallback(
    ({ id }) => {
      setRateModalProps({
        ratingId: id,
        onRate: handleEditRate(id),
      });
      setRateModalOpen(true);
    },
    [handleEditRate, setRateModalOpen, setRateModalProps]
  );

  const handleDeleteRating = useCallback(
    ({ id }) => {
      const onAccept = async () => {
        const { success } = await DashboardUserRatingService.deleteUserRating(id);
        if (success) {
          await mutate();
          await fetchRatings();
          toast.success('Rating deleted successfully!');
          closeDialog();
        }
      };

      openDialog({
        acceptText: 'Delete Rating',
        cancelText: 'Discard',
        onAccept,
      });
    },
    [closeDialog, fetchRatings, mutate, openDialog]
  );

  const handleOpenRateModal = useCallback(() => {
    setRateModalProps({
      onRate: handleRate,
    });
    setRateModalOpen(true);
  }, [handleRate, setRateModalOpen, setRateModalProps]);

  const handleAnswer = useCallback(
    async ({ id, answer }) => {
      const data = { answer };
      const { success } = await DashboardUserGroupsContentsUserRatingsService.postUserRatingsAnswer(
        contentId,
        id,
        data
      );

      if (success) {
        toast.success('Successfully answered!');
        await deleteAndMutate({ id });
      }
    },
    [contentId, deleteAndMutate]
  );

  const handleEditAnswer = useCallback(
    async ({ id, answer }) => {
      const data = { answer };
      const { success } = await DashboardUserGroupsContentsUserRatingsService.putUserRatingsAnswer(
        contentId,
        id,
        data
      );

      if (success) {
        toast.success('Successfully updated your answer!');
        await deleteAndMutate({ id });
      }
    },
    [contentId, deleteAndMutate]
  );

  const handleDeleteAnswer = useCallback(
    ({ id }) => {
      const onAccept = async () => {
        const { success } =
          await DashboardUserGroupsContentsUserRatingsService.deleteUserRatingsAnswer(
            contentId,
            id
          );

        if (success) {
          toast.success('Successfully deleted your answer!');
          await deleteAndMutate({ id });
        }
        closeDialog();
      };
      openDialog({
        title: 'Are you sure you want to delete your answer?',
        acceptText: 'Delete Answer',
        cancelText: 'Discard',
        onAccept,
      });
    },
    [closeDialog, contentId, deleteAndMutate, openDialog]
  );

  const { loading } = useFetch(fetchRatings);

  const total = useMemo(
    () =>
      Object.values(ratingStatistics).reduce(
        (total, val) => (!isNaN(val) ? total + val : total),
        0
      ),
    [ratingStatistics]
  );

  const userRating = useMemo(() => ratingStatistics?.userRating, [ratingStatistics?.userRating]);
  if (loading)
    return (
      <Container maxWidth="lg" sx={{ my: 4, height: 300 }}>
        <Loading />
      </Container>
    );
  return (
    <Container maxWidth="lg" sx={{ my: 6 }}>
      <Grid container alignItems="center" justifyContent="center">
        <Grid item container xs={12} md={5} alignItems="flex-start" direction="column">
          <Typography variant="h5">{ratingCount} reviews for this content</Typography>
          <Stack direction="row" alignItems="center">
            <Rating readOnly value={ratingScore ?? 0} precision={0.1} size="small" /> {ratingScore}{' '}
            out of 5
          </Stack>
          <Box sx={{ width: '100%' }}>
            {Object.entries(ratingStatistics)
              .filter((o) => !isNaN(o[0]))
              .sort((a, b) => b[0] - a[0])
              .map(([rating, value]) => (
                <Grid
                  container
                  item
                  alignItems="center"
                  justifyContent="stretch"
                  spacing={1}
                  sx={{ my: 1 }}
                  key={`rating-${rating}`}
                >
                  <Grid item>
                    <Typography variant="subtitle2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {rating} star
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <LinearProgress
                      sx={{ width: '100%' }}
                      variant="determinate"
                      value={total ? (value / total) * 100 : 0}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <Typography variant="subtitle2">{value}</Typography>
                  </Grid>
                </Grid>
              ))}
          </Box>
        </Grid>
        <Grid item container xs={12} md={5}>
          {!userRating && (
            <Stack spacing={2}>
              <Typography variant="h5">Review this content</Typography>
              <Typography variant="subtitle1">Share your thoughts with other customers</Typography>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleOpenRateModal}
                disabled={!ratingActive}
              >
                Write a customer review
              </Button>
              <MuiLink
                onClick={handleToggleReviewInfo}
                sx={{
                  '&:hover': {
                    cursor: 'pointer',
                  },
                }}
                color="secondary"
                alignItems="center"
                display="flex"
              >
                {reviewInfoBoxOpen ? <ExpandLess /> : <ExpandMore />}
                How user reviews and ratings work
              </MuiLink>
              {reviewInfoBoxOpen && (
                <Paper component={Stack} spacing={2}>
                  <Typography>
                    Only users who have registered event, ordered services or purchased products are
                    able to leave a User Review.
                  </Typography>
                  <Typography>Star Ratings</Typography>
                  <Typography>
               {`To calculate the overall star rating and percentage breakdown by star, we don't
                    use a simple average. Instead, our system considers things like how recent a
                    review is and if the reviewer bought the services on Scaleupstream. It also
                    analyses reviews to verify trustworthiness.`}     
                  </Typography>
                </Paper>
              )}
            </Stack>
          )}
        </Grid>
      </Grid>
      {userRating && (
        <Card sx={{ my: 1 }}>
          <CardHeader title="Your Review" />
          <CardContent>
            <List disablePadding>
              <UserRating
                showStatus
                userRating={userRating}
                onEdit={handleEditRating}
                onDelete={handleDeleteRating}
              />
            </List>
          </CardContent>
        </Card>
      )}
      {Boolean(ratingList?.length) && (
        <Card>
          <CardHeader title="User Reviews" />
          <CardContent>
            <List disablePadding>
              {ratingList?.map((userRating) => (
                <Box key={`content-rating-${userRating?.id}`} sx={{ my: 1 }}>
                  <UserRating
                    userRating={userRating}
                    userGroupId={userGroupId}
                    onAnswer={handleAnswer}
                    onEditAnswer={handleEditAnswer}
                    onDeleteAnswer={handleDeleteAnswer}
                  />
                </Box>
              ))}
            </List>
            {page < ratingListMeta?.totalPages ? (
              <Stack justifyContent="center" alignItems="center">
                <Button onClick={handleViewMoreRatings}>View More</Button>
              </Stack>
            ) : (
              <Typography variant="h6" textAlign="center">
                No more ratings!
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Container>
  );
}
